// Two cases for one client is a real shape in this firm's data: a folder
// synced twice, a client re-entered by hand. The manager only finds out
// here, on the matter they already have open, so this says which twin and
// what matched, and lets an admin merge the two from the same spot.

import { useState } from "react";
import { EntityLink } from "../chrome/EntityLink";
import { useCurrentUser } from "../auth/CurrentUserContext";
import { isAdminRole } from "../../lib/roles";
import { useCaseTwins, useDismissTwin } from "../../hooks/useCaseTwins";
import { MergeDialog } from "./MergeDialog";
import type { CaseTwin } from "../../api/duplicates";
import { matchPhrase } from "../../lib/duplicateWords";

export function DuplicateNotice({ caseId, eID }: { caseId: string; eID: string }) {
  const twins = useCaseTwins(caseId);
  const dismiss = useDismissTwin(caseId);
  const isAdmin = isAdminRole(useCurrentUser()?.role);
  const [merging, setMerging] = useState<CaseTwin | null>(null);

  // The merge's own summary outlives the row it came from: the twin stops
  // being reported the moment the merge lands, and the dialog must not go
  // with it.
  const dialog = merging && (
    <MergeDialog
      open
      onClose={() => setMerging(null)}
      current={{ caseId, eId: eID }}
      twin={merging}
    />
  );

  // A scan that could not run says nothing: a matter card is not the place
  // to report on the duplicate checker's health. The dialog keeps its
  // place in the tree either way, or React would remount it mid-merge.
  const card = twins.data && twins.data.length > 0 && (
    <div
      className="cc-card"
      // A full amber outline: the practice-area accent already owns the top edge.
      style={{ padding: "12px 18px", marginBottom: 12, border: "1px solid var(--warn-600)" }}
    >
      {twins.data.map((twin) => (
        <div
          key={twin.caseId}
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <span>
            <b style={{ color: "var(--warn-600)" }}>Possible duplicate</b>{" "}
            <EntityLink kind="matter" id={twin.caseId} eID={twin.eId ?? twin.caseId}>
              {twin.name}
            </EntityLink>{" "}
            <code>{twin.eId ?? "no eID"}</code> has {matchPhrase(twin.matchedBy)}.
          </span>
          {isAdmin && (
            <span style={{ display: "flex", gap: 8 }}>
              <button type="button" className="cc-link-btn" onClick={() => setMerging(twin)}>
                Merge...
              </button>
              <button
                type="button"
                className="cc-link-btn"
                disabled={dismiss.isPending}
                onClick={() => dismiss.mutate(twin.caseId)}
              >
                Not the same person
              </button>
            </span>
          )}
        </div>
      ))}
      {!isAdmin && (
        <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 8 }}>
          An admin can merge the two, or mark them as different people, from here.
        </div>
      )}
    </div>
  );

  return (
    <>
      {card}
      {dialog}
    </>
  );
}
